import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type {
  ApiResponse,
  CreateClubModel,
  CreateClubResponse,
  ClubDetailsDTO,
  JoinClubResponse,
  LeaveClubResponse,
  UpdateClubModel,
  UpdateClubResponse,
} from "@membentry/shared-types";
import type { AxiosInstance } from "axios";
import { CLUB_QUERY_KEY } from "./queryKeys";
import {
  invalidateClubById,
  invalidateClubMembership,
  invalidateClubs,
  invalidateMyClub,
  invalidateMyClubs,
} from "./invalidation";

type MutationSuccessHandler<TData, TVariables> = NonNullable<
  UseMutationOptions<TData, Error, TVariables>["onSuccess"]
>;

const withOnSuccess = <TData, TVariables>(
  base: MutationSuccessHandler<TData, TVariables>,
  extra?: MutationSuccessHandler<TData, TVariables>,
): MutationSuccessHandler<TData, TVariables> => {
  return (data, variables, onMutateResult, context) => {
    base(data, variables, onMutateResult, context);
    return extra?.(data, variables, onMutateResult, context);
  };
};

export function useCreateClubMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<CreateClubResponse, Error, CreateClubModel>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateClubModel) => {
      const { createClub } = await import("@membentry/shared-api");
      return createClub(client, data);
    },
    onSuccess: withOnSuccess(() => {
      invalidateClubs(queryClient);
      invalidateMyClub(queryClient);
    }, options?.onSuccess),
    ...options,
  });
}

export function useUpdateClubMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      UpdateClubResponse,
      Error,
      { id: number; data: Partial<UpdateClubModel> }
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<UpdateClubModel>;
    }) => {
      const { updateClub } = await import("@membentry/shared-api");
      return updateClub(client, id, data);
    },
    onSuccess: withOnSuccess(() => {
      invalidateClubs(queryClient);
      invalidateMyClub(queryClient);
      queryClient.invalidateQueries({ queryKey: [CLUB_QUERY_KEY] });
    }, options?.onSuccess),
    ...options,
  });
}

export function useUpdateClubLogoMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<ClubDetailsDTO>,
      Error,
      { id: number; logoImageUrl: string }
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      logoImageUrl,
    }: {
      id: number;
      logoImageUrl: string;
    }) => {
      const { updateClubLogo } = await import("@membentry/shared-api");
      return updateClubLogo(client, id, logoImageUrl);
    },
    onSuccess: withOnSuccess((_data, variables) => {
      invalidateMyClub(queryClient);
      invalidateClubById(queryClient, variables.id);
      invalidateClubs(queryClient);
    }, options?.onSuccess),
    ...options,
  });
}

export function useJoinClubMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<JoinClubResponse, Error, number>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (clubId: number) => {
      const { joinClub } = await import("@membentry/shared-api");
      return joinClub(client, clubId);
    },
    onSuccess: withOnSuccess(() => {
      invalidateClubs(queryClient);
      invalidateMyClubs(queryClient);
      invalidateMyClub(queryClient);
    }, options?.onSuccess),
  });
}

export function useLeaveClubMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<LeaveClubResponse, Error, number>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (clubId: number) => {
      const { leaveClub } = await import("@membentry/shared-api");
      return leaveClub(client, clubId);
    },
    onSuccess: withOnSuccess((_data, clubId) => {
      invalidateClubs(queryClient);
      invalidateMyClubs(queryClient);
      invalidateMyClub(queryClient);
      invalidateClubById(queryClient, clubId);
      invalidateClubMembership(queryClient, clubId);
    }, options?.onSuccess),
  });
}
